/**
 * Authentication API
 *
 * Mock authentication service with localStorage persistence.
 * Implements login, register, logout, password reset, and token management.
 */

import type {
  User,
  AccountTier,
  ConsentStatus,
  AuthResponse,
  AuthTokens,
  LoginData,
  RegisterData,
  PasswordResetRequest,
  PasswordResetConfirm,
  ChangePasswordData,
  TokenRefreshResponse,
  ApiResponse,
} from '../types'
import { mockDb, generateId, delay } from './mockDb'
import {
  success,
  error,
  mockHash,
  mockVerify,
  generateMockToken,
  isTokenExpired,
  isValidEmail,
  isValidPassword,
  calculateAge,
} from './client'

// Login rate limiting (in-memory, resets on page refresh)
const loginAttempts = new Map<string, { count: number; firstAttempt: number }>()

// Session storage key
const SESSION_KEY = 'docurious-session'

/**
 * Internal user record with password hash
 */
interface UserRecord extends User {
  passwordHash: string
  passwordResetToken?: string
  passwordResetExpires?: string
}

/**
 * Get current session from storage
 */
function getCurrentSession(): { userId: string; tokens: AuthTokens } | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null

  try {
    const session = JSON.parse(stored)
    if (isTokenExpired(session.tokens.accessToken)) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    return session
  } catch {
    return null
  }
}

/**
 * Save session to storage
 */
function saveSession(userId: string, tokens: AuthTokens): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify({ userId, tokens }))
}

/**
 * Clear session from storage
 */
function clearSession(): void {
  localStorage.removeItem(SESSION_KEY)
}

/**
 * Generate auth tokens for a user
 */
function generateTokens(userId: string): AuthTokens {
  const accessToken = generateMockToken(userId, 24) // 24 hours
  const refreshToken = generateMockToken(userId, 168) // 7 days

  return {
    accessToken,
    refreshToken,
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  }
}

/**
 * Convert UserRecord to User (strip password)
 */
function toPublicUser(record: UserRecord): User {
  const { passwordHash, passwordResetToken, passwordResetExpires, ...user } = record
  return user
}

/**
 * Authentication API
 */
export const authApi = {
  /**
   * Register a new user
   */
  async register(data: RegisterData): Promise<ApiResponse<AuthResponse>> {
    await delay(150)

    // Validate email
    if (!isValidEmail(data.email)) {
      return error('Invalid email format')
    }

    // Check if email already exists
    const existingUser = mockDb.findOne<UserRecord>('users', (u) => u.email === data.email)
    if (existingUser) {
      return error('An account with this email already exists')
    }

    // Validate password
    const passwordCheck = isValidPassword(data.password)
    if (!passwordCheck.valid) {
      return error(passwordCheck.message!)
    }

    // Calculate age and determine account tier
    const age = calculateAge(data.dateOfBirth)
    const isUnder13 = age < 13
    let accountTier: AccountTier = 'standard'
    let consentStatus: ConsentStatus = 'not_required'

    if (isUnder13) {
      // Under-13 needs either school or parent consent
      if (data.schoolCode) {
        accountTier = 'tier_1_school_only'
        consentStatus = 'not_required' // School acts as agent
      } else if (data.parentEmail) {
        accountTier = 'tier_2_full'
        consentStatus = 'pending' // Needs parent approval
      } else {
        return error('Users under 13 must register through a school or with parent consent')
      }
    }

    // COPPA: strip optional PII fields for under-13 registrations (data minimization)
    const userFields: Record<string, any> = {
      email: data.email,
      passwordHash: mockHash(data.password),
      displayName: data.displayName,
      dateOfBirth: data.dateOfBirth,
      role: data.role ?? 'user',
      accountTier,
      accountStatus: 'active',
      isUnder13,
      parentalConsentStatus: consentStatus,
      interests: data.interests ?? [],
      strengths: [],
      aspirations: [],
      onboardingCompleted: false,
    }
    if (isUnder13) {
      // Only collect what's necessary — no phone, location, or emergency contact
      delete userFields.phone
      delete userFields.location
      delete userFields.emergencyContact
      delete userFields.address
    }

    // Create user record
    const userRecord = mockDb.insert<UserRecord>('users', userFields)

    // Generate tokens and save session
    const tokens = generateTokens(userRecord.id)
    saveSession(userRecord.id, tokens)

    return success({
      user: toPublicUser(userRecord),
      tokens,
    })
  },

  /**
   * Login with email and password
   */
  async login(data: LoginData): Promise<ApiResponse<AuthResponse>> {
    await delay(100)

    // Rate limiting: check for too many failed attempts
    const attempts = loginAttempts.get(data.email)
    const LOCKOUT_WINDOW = 15 * 60 * 1000 // 15 minutes
    const MAX_ATTEMPTS = 5

    if (attempts && attempts.count >= MAX_ATTEMPTS) {
      const elapsed = Date.now() - attempts.firstAttempt
      if (elapsed < LOCKOUT_WINDOW) {
        const remainingMinutes = Math.ceil((LOCKOUT_WINDOW - elapsed) / 60000)
        return error(`Too many login attempts. Please try again in ${remainingMinutes} minute(s).`)
      }
      // Window has passed — reset
      loginAttempts.delete(data.email)
    }

    // Find user by email
    const userRecord = mockDb.findOne<UserRecord>('users', (u) => u.email === data.email)

    if (!userRecord) {
      // Track failed attempt
      const entry = loginAttempts.get(data.email) ?? { count: 0, firstAttempt: Date.now() }
      entry.count += 1
      loginAttempts.set(data.email, entry)
      return error('Invalid email or password')
    }

    // Verify password
    if (!mockVerify(data.password, userRecord.passwordHash)) {
      // Track failed attempt
      const entry = loginAttempts.get(data.email) ?? { count: 0, firstAttempt: Date.now() }
      entry.count += 1
      loginAttempts.set(data.email, entry)
      return error('Invalid email or password')
    }

    // Check account status
    if (userRecord.accountStatus === 'deactivated') {
      return error('This account has been deactivated')
    }
    if (userRecord.accountStatus === 'dormant') {
      return error('This account is dormant. Please contact support.')
    }
    if (userRecord.accountStatus === 'pending_deletion') {
      return error('This account is scheduled for deletion')
    }

    // Successful login — clear rate-limit tracking
    loginAttempts.delete(data.email)

    // Generate tokens and save session
    const tokens = generateTokens(userRecord.id)
    saveSession(userRecord.id, tokens)

    return success({
      user: toPublicUser(userRecord),
      tokens,
    })
  },

  /**
   * Login with Google OAuth (mock implementation)
   */
  async googleLogin(data: { credential: string }): Promise<ApiResponse<AuthResponse>> {
    await delay(200)

    // In mock, we'll create/find a user based on a mock email derived from the credential
    // In real implementation, the credential would be validated with Google's servers
    const mockEmail = `google_user_${data.credential.slice(0, 8)}@example.com`
    const mockDisplayName = 'Google User'

    // Check if user exists
    let userRecord = mockDb.findOne<UserRecord>('users', (u) => u.email === mockEmail)

    if (!userRecord) {
      // Create new user
      userRecord = mockDb.insert<UserRecord>('users', {
        email: mockEmail,
        passwordHash: mockHash('google_oauth_' + data.credential), // Not used for OAuth
        displayName: mockDisplayName,
        dateOfBirth: '1990-01-01', // Default for OAuth users
        role: 'user',
        accountTier: 'standard',
        accountStatus: 'active',
        isUnder13: false,
        parentalConsentStatus: 'not_required',
        interests: [],
        strengths: [],
        aspirations: [],
        onboardingCompleted: false,
      })
    }

    // Check account status
    if (userRecord.accountStatus === 'deactivated') {
      return error('This account has been deactivated')
    }

    const tokens = generateTokens(userRecord.id)
    saveSession(userRecord.id, tokens)

    return success({
      user: toPublicUser(userRecord),
      tokens,
    })
  },

  /**
   * Login with Apple Sign-In (mock implementation)
   */
  async appleLogin(data: {
    idToken: string
    authorizationCode: string
    user?: {
      email?: string
      name?: {
        firstName?: string
        lastName?: string
      }
    }
  }): Promise<ApiResponse<AuthResponse>> {
    await delay(200)

    // In mock, we'll use the provided email or derive one from the token
    // In real implementation, the token would be validated with Apple's servers
    const email = data.user?.email || `apple_user_${data.idToken.slice(0, 8)}@privaterelay.appleid.com`
    const displayName = data.user?.name
      ? `${data.user.name.firstName || ''} ${data.user.name.lastName || ''}`.trim() || 'Apple User'
      : 'Apple User'

    // Check if user exists
    let userRecord = mockDb.findOne<UserRecord>('users', (u) => u.email === email)

    if (!userRecord) {
      // Create new user
      userRecord = mockDb.insert<UserRecord>('users', {
        email,
        passwordHash: mockHash('apple_oauth_' + data.idToken), // Not used for OAuth
        displayName,
        dateOfBirth: '1990-01-01', // Default for OAuth users
        role: 'user',
        accountTier: 'standard',
        accountStatus: 'active',
        isUnder13: false,
        parentalConsentStatus: 'not_required',
        interests: [],
        strengths: [],
        aspirations: [],
        onboardingCompleted: false,
      })
    }

    // Check account status
    if (userRecord.accountStatus === 'deactivated') {
      return error('This account has been deactivated')
    }

    const tokens = generateTokens(userRecord.id)
    saveSession(userRecord.id, tokens)

    return success({
      user: toPublicUser(userRecord),
      tokens,
    })
  },

  /**
   * Logout current user
   */
  async logout(): Promise<ApiResponse<void>> {
    await delay(50)
    clearSession()
    return success(undefined)
  },

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<ApiResponse<User | null>> {
    await delay(50)

    const session = getCurrentSession()
    if (!session) {
      return success(null)
    }

    const userRecord = mockDb.findById<UserRecord>('users', session.userId)
    if (!userRecord) {
      clearSession()
      return success(null)
    }

    return success(toPublicUser(userRecord))
  },

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<ApiResponse<TokenRefreshResponse>> {
    await delay(50)

    if (isTokenExpired(refreshToken)) {
      clearSession()
      return error('Refresh token expired. Please login again.')
    }

    const session = getCurrentSession()
    if (!session) {
      return error('No active session')
    }

    const tokens = generateTokens(session.userId)
    saveSession(session.userId, tokens)

    return success({ tokens })
  },

  /**
   * Request password reset
   */
  async forgotPassword(data: PasswordResetRequest): Promise<ApiResponse<void>> {
    await delay(100)

    // Always return success to prevent email enumeration
    const userRecord = mockDb.findOne<UserRecord>('users', (u) => u.email === data.email)

    if (userRecord) {
      // Generate reset token
      const resetToken = generateId()
      const expires = new Date(Date.now() + 60 * 60 * 1000).toISOString() // 1 hour

      mockDb.update<UserRecord>('users', userRecord.id, {
        passwordResetToken: resetToken,
        passwordResetExpires: expires,
      })

      // In a real app, this would send an email
      console.log(`Password reset token for ${data.email}: ${resetToken}`)
    }

    return success(undefined)
  },

  /**
   * Reset password with token
   */
  async resetPassword(data: PasswordResetConfirm): Promise<ApiResponse<void>> {
    await delay(100)

    // Validate password
    const passwordCheck = isValidPassword(data.newPassword)
    if (!passwordCheck.valid) {
      return error(passwordCheck.message!)
    }

    if (data.newPassword !== data.confirmPassword) {
      return error('Passwords do not match')
    }

    // Find user with matching reset token
    const userRecord = mockDb.findOne<UserRecord>(
      'users',
      (u) => u.passwordResetToken === data.token
    )

    if (!userRecord) {
      return error('Invalid or expired reset token')
    }

    // Check if token is expired
    if (
      userRecord.passwordResetExpires &&
      new Date(userRecord.passwordResetExpires) < new Date()
    ) {
      return error('Reset token has expired')
    }

    // Update password and clear reset token
    mockDb.update<UserRecord>('users', userRecord.id, {
      passwordHash: mockHash(data.newPassword),
      passwordResetToken: undefined,
      passwordResetExpires: undefined,
    })

    return success(undefined)
  },

  /**
   * Change password (while logged in)
   */
  async changePassword(data: ChangePasswordData): Promise<ApiResponse<void>> {
    await delay(100)

    const session = getCurrentSession()
    if (!session) {
      return error('Not authenticated')
    }

    const userRecord = mockDb.findById<UserRecord>('users', session.userId)
    if (!userRecord) {
      return error('User not found')
    }

    // Verify current password
    if (!mockVerify(data.currentPassword, userRecord.passwordHash)) {
      return error('Current password is incorrect')
    }

    // Validate new password
    const passwordCheck = isValidPassword(data.newPassword)
    if (!passwordCheck.valid) {
      return error(passwordCheck.message!)
    }

    if (data.newPassword !== data.confirmPassword) {
      return error('New passwords do not match')
    }

    // Update password
    mockDb.update<UserRecord>('users', userRecord.id, {
      passwordHash: mockHash(data.newPassword),
    })

    return success(undefined)
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return getCurrentSession() !== null
  },

  /**
   * Get current session tokens
   */
  getTokens(): AuthTokens | null {
    const session = getCurrentSession()
    return session?.tokens ?? null
  },
}
